import z from 'zod';
import { statusPackWarningsSchema } from './status-pack-warning.schema.js';
// DR-5186: scanned-page SB-number cell re-read outcome, on rows and on occurrences.
const sbNumberCheckFields = {
    sbNumberCheck: z.enum(['agree', 'disagree', 'unreadable', 'not-located', 'skipped', 'replaced']).optional(),
    sbNumberUncertain: z.literal(true).optional(),
    sbNumberCandidates: z.array(z.string()).optional(),
};
export const sbStatusEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('sb-status-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    documentId: z.string(),
    pageIds: z.array(z.string()),
    sbs: z.array(z.looseObject({
        sbNumber: z.string(),
        ...sbNumberCheckFields,
        revision: z.string().optional(),
        // DR-5720: an operator part / variant marker printed after the bulletin number, e.g. `MW326`
        // (TRAX "CFM56-7B S/B 72-1010 MW326"), peeled off `sbNumber` so the number matches the
        // catalogue. Declared explicitly rather than left to the loose object, so it is part of the
        // typed wire contract. Absent when none. Rows of one bulletin with a different `requirement`
        // are different lines of that bulletin, each with its own compliance: group them under one
        // SB, never collapse them on `sbNumber` + `revision`.
        requirement: z.string().optional(),
        // The verbatim compliance-category manufacturer code (normalisation deferred to Oil, DR-2035).
        categoryRaw: z.string().optional(),
        status: z.string().optional(),
        // DR-5895: the source status cell verbatim, set only when Brain re-mapped it to `status` (a TRAX
        // P/N Detail Print `TERMINATED` publishes `canceled`, never complied). It belongs to the record
        // whose `status` the row publishes. Absent otherwise.
        statusRaw: z.string().optional(),
        // DR-5895 (Rinoj, 2026-10-28): the SB is tracked on this component (a TRAX P/N Detail Print
        // component block). Show the row as a reference-only line pointing at the component's QEC row
        // ("Tracked on component — AIR STARTER S/N ABCD1234, see QEC"): its status belongs to that
        // component S/N, and an open one is never missing or overdue at engine level. Absent otherwise.
        trackedOnComponent: z
            .looseObject({ partNumber: z.string(), serialNumber: z.string(), description: z.string().optional() })
            .optional(),
        // DR-5452: document-stated or interval-derived task nature; absent when unstated.
        isRepeated: z.boolean().optional(),
        // DR-2614/DR-2597: contradiction flag between the row's status and its Remarks text.
        remarksStatus: z.string().optional(),
        subject: z.string().optional(),
        // Associated AD reference(s) so oil can cross-link SB <-> AD compliance.
        adReference: z.string().optional(),
        // DR-2238: catch-all superset of ALL document references on the row; adReference holds
        // only validated AD-shaped identifiers, rejected tokens migrate here.
        reference: z.array(z.string()).optional(),
        ataReference: z.string().optional(),
        applicableEngine: z.string().optional(),
        // Per-part SB rows (AMOS prints one row per part).
        partNumber: z.string().optional(),
        serialNumber: z.string().optional(),
        complianceDate: z.string().optional(),
        complianceTsn: z.number().optional(),
        complianceCsn: z.number().optional(),
        complianceShopVisit: z.string().optional(),
        nextDueCycles: z.number().optional(),
        nextDueHours: z.number().optional(),
        nextDueDate: z.string().optional(),
        nextDueShopVisit: z.string().optional(),
        nextDueAtShopVisit: z.boolean().optional(),
        // DR-6111: the count of cycles / flight hours still to run until the next compliance, AS THE
        // SHEET PRINTS IT ("required within 1598 Cycles" -> `remainingCycles: 1598`), measured from the
        // sheet's own counters. The count is the row's own, from the winning document (never filled
        // from another document); every document's count also rides its own `occurrences[]` entry.
        // Resolve the basis through `remainingReconstructedDocumentId`: the document (and its
        // `occurrences[]` entry) with that id, whose header TSN / CSN / as-of date
        // `document-header-ready` carries, not `sbPageIds`. Brain does no arithmetic. Absent when the sheet prints an absolute due or no due;
        // never null. Open rows only. A printed count is never in both a remaining lane and the matching
        // nextDue lane, except on a zero-time engine: a page header counter of exactly 0 may keep an
        // equal absolute beside it. A remaining count of 0 is not representable (absent).
        remainingCycles: z.number().optional(),
        remainingHours: z.number().optional(),
        // DR-6111: the reconstructed document the remaining count was read from (the same id its
        // `occurrences[]` entry carries). Present exactly when `remainingCycles` or `remainingHours` is.
        remainingReconstructedDocumentId: z.string().optional(),
        repeatIntervalCycles: z.number().optional(),
        repeatIntervalHours: z.number().optional(),
        repeatIntervalDays: z.number().optional(),
        repeatIntervalShopVisits: z.number().optional(),
        remarks: z.string().optional(),
        sbPageIds: z.array(z.string()),
        // DR-5056: who issued the SOURCE document of this row's fold winner, read from its header. A
        // row never blends an older document's provenance in. Tri-state `issuerRole`: absent means the
        // document states no signal; Brain never defaults it, the consumer applies its own default.
        // `issuerName` / `workOrder` are verbatim header values.
        issuerRole: z.enum(['operator', 'shop']).optional(),
        issuerName: z.string().optional(),
        workOrder: z.string().optional(),
        // DR-5078: `'failed'` when the source document's header extraction failed and its pages agree
        // on no `issuerRole`: an absent role then means "not read", not "not stated".
        provenanceStatus: z.enum(['failed']).optional(),
        // DR-5550: is the SOURCE document of this row's fold winner signed or stamped? Document-scoped:
        // `true` when at least one page of the document shows a completed signature or stamp, `false`
        // when every page was read and none does. Not line certification (the job-card `signedOff` is).
        // Tri-state: `true` signed, `false` read and not signed, absent = unknown, never `null`.
        // Unlike `compliance-job-card-facts-ready`, where `false` and `null` both mean 'not
        // certified', absent here is NOT 'not signed'. A non-boolean value parses as absent.
        signedOff: z.boolean().optional().catch(undefined),
        // DR-5551 (Rinoj 2026-10-21): this closed line itself states the work was complied with BEFORE
        // this shop visit ("Previously Complied With", PW, PE, PC, PCW, ASY, PREV, or a remarks phrase
        // such as "C/W prior to induction" / "done at previous shop visit"). Additive, not a status
        // change: `status` stays as printed (closed). `quote` is the status cell as printed, or the
        // remarks sentence that carried the wording. Row evidence: it travels only with its own line.
        // Absent = no such wording, or unknown. A malformed value parses as absent.
        compliedBeforeVisit: z.looseObject({ quote: z.string() }).optional().catch(undefined),
        // DR-6112: the sheet's own status cell ("NP", "Not performed") or this line's own remark ("refer
        // to Open Item List for further action") states the work was NOT done at this visit. Additive,
        // not a status change: `status` stays open. True-only: `true` or absent, never `false`. Row
        // evidence, never filled from another row or document; a missing compliance date never sets it,
        // and a remark stating the work was done (`remarksStatus: 'closed'`) clears it. Oil writes
        // `notPerformed ?? null`.
        notPerformed: z.literal(true).optional(),
        // DR-5056: the `occurrences` entries this row covers. Brain always sends it; optional in the schema only so envelopes from an older Brain still parse.
        // Partition invariant: every id here names an entry of `occurrences`, and each occurrence id
        // sits in exactly one row. Oil quarantines a row with a dangling or repeated id.
        occurrenceIds: z.array(z.string()).optional(),
    })),
    // DR-4456: review flags on the published pack (see `statusPackWarningsSchema`).
    warnings: statusPackWarningsSchema,
    // DR-5056: every SB row of every source document, taken BEFORE the per-document revision collapse
    // and the cross-document fold that shape `sbs`. One entry per row per document, with that
    // document's own issuer provenance, for proof-found / proof-missing and the `workOrder` join to the
    // release certificate. `sbNumber` / `revision` are the page row's values exactly as extracted; the
    // relabelled spelling lives on `sbs` only, and `occurrenceId` is the join. Brain always sends it —
    // `[]` when no row survives, never absent — so an absent array means an older Brain. Optional only
    // so this schema still parses those envelopes.
    occurrences: z
        .array(z.looseObject({
        occurrenceId: z.string(),
        reconstructedDocumentId: z.string(),
        documentId: z.string().optional(),
        pageIds: z.array(z.string()),
        sbNumber: z.string(),
        ...sbNumberCheckFields,
        revision: z.string().optional(),
        // DR-5720: the row's operator part / variant marker (see the `sbs` row field). Absent when none.
        requirement: z.string().optional(),
        status: z.string().optional(),
        remarksStatus: z.string().optional(),
        categoryRaw: z.string().optional(),
        subject: z.string().optional(),
        adReference: z.string().optional(),
        reference: z.array(z.string()).optional(),
        ataReference: z.string().optional(),
        applicableEngine: z.string().optional(),
        partNumber: z.string().optional(),
        serialNumber: z.string().optional(),
        complianceDate: z.string().optional(),
        complianceTsn: z.number().optional(),
        complianceCsn: z.number().optional(),
        complianceShopVisit: z.string().optional(),
        // DR-6111: this document's printed remaining count (see the row field). Its basis is this
        // occurrence's document header (`document-header-ready`). Absent when none; never null.
        remainingCycles: z.number().optional(),
        remainingHours: z.number().optional(),
        issuerRole: z.enum(['operator', 'shop']).optional(),
        issuerName: z.string().optional(),
        workOrder: z.string().optional(),
        provenanceStatus: z.enum(['failed']).optional(),
        // DR-5550: is this occurrence's document signed or stamped? Document-scoped: `true` when at
        // least one page of the document shows a completed signature or stamp, `false` when every
        // page was read and none does. Not line certification (the job-card `signedOff` is).
        // Tri-state: `true` signed, `false` read and not signed, absent = unknown, never `null`.
        // Unlike `compliance-job-card-facts-ready`, where `false` and `null` both mean 'not
        // certified', absent here is NOT 'not signed'. A non-boolean value parses as absent.
        signedOff: z.boolean().optional().catch(undefined),
        // DR-5551: this document's line states compliance before this shop visit (see the row field).
        // Additive, not a status change. Absent = no such wording, or unknown. A malformed value parses
        // as absent.
        compliedBeforeVisit: z.looseObject({ quote: z.string() }).optional().catch(undefined),
        // DR-6112: this document's line states the work was not done at this visit (see the row
        // field). True-only. Oil writes `notPerformed ?? null`.
        notPerformed: z.literal(true).optional(),
    }))
        .optional(),
});
//# sourceMappingURL=sb-status-events-ready.webhook.js.map